import type { ProvetPayPayoutItemAttributes } from '@/models/reports/provetPayPayoutReport.js'

export const PROVET_PAY_PAYOUT_STALE_TIME = 1000 * 5 * 60 // 5 minutes

export const PROVET_PAY_PAYOUT_RESOURCE_TYPES = {
  FILTERS: 'ProvetpayPayoutFilters',
  GROUP: 'ProvetpayPayoutAggregationGroup',
  PAYOUT: 'ProvetpayPayout',
} as const

export const PROVET_PAY_PAYOUT_CHANNEL = {
  ECOM: 'ECOM',
  POS: 'POS',
} as const

export const PROVET_PAY_PAYOUT_REPORT_COLUMNS = {
  Channel: {
    id: 'channel',
    accessorKey: 'attributes.channel',
  },
  ClinicLocation: {
    id: 'clinic_location',
    accessorKey: 'attributes.clinic_location',
  },
  CurrencyCode: {
    id: 'currency',
    accessorKey: 'attributes.currency',
  },
  Fees: {
    id: 'fees',
    accessorKey: 'attributes.fees',
  },
  GrossAmount: {
    id: 'gross_amount',
    accessorKey: 'attributes.gross_amount',
  },
  NetPayoutAmount: {
    id: 'net_payout_amount',
    accessorKey: 'attributes.net_payout_amount',
  },
  PayoutDate: {
    id: 'payout_ts_utc',
    accessorKey: 'attributes.payout_ts_utc',
  },
  PayoutPspReference: {
    id: 'payout_psp_reference',
    accessorKey: 'attributes.payout_psp_reference',
  },
  Refunds: {
    id: 'refunds',
    accessorKey: 'attributes.refunds',
  },
  TransactionCount: {
    id: 'transaction_count',
    accessorKey: 'attributes.transaction_count',
  },
} as const

export const PROVET_PAY_PAYOUT_GROUPABLE_COLUMNS = [
  PROVET_PAY_PAYOUT_REPORT_COLUMNS.ClinicLocation.id,
  PROVET_PAY_PAYOUT_REPORT_COLUMNS.Channel.id,
] as const

export const PROVET_PAY_PAYOUT_DEFAULT_COLUMN_ORDER = [
  PROVET_PAY_PAYOUT_REPORT_COLUMNS.PayoutDate.id,
  PROVET_PAY_PAYOUT_REPORT_COLUMNS.ClinicLocation.id,
  PROVET_PAY_PAYOUT_REPORT_COLUMNS.Channel.id,
  PROVET_PAY_PAYOUT_REPORT_COLUMNS.PayoutPspReference.id,
  PROVET_PAY_PAYOUT_REPORT_COLUMNS.TransactionCount.id,
  PROVET_PAY_PAYOUT_REPORT_COLUMNS.GrossAmount.id,
  PROVET_PAY_PAYOUT_REPORT_COLUMNS.Fees.id,
  PROVET_PAY_PAYOUT_REPORT_COLUMNS.Refunds.id,
  PROVET_PAY_PAYOUT_REPORT_COLUMNS.NetPayoutAmount.id,
  PROVET_PAY_PAYOUT_REPORT_COLUMNS.CurrencyCode.id,
] as const

export const PROVET_PAY_PAYOUT_DEFAULT_HIDDEN_COLUMNS = {
  [PROVET_PAY_PAYOUT_REPORT_COLUMNS.Channel.id]: false,
  [PROVET_PAY_PAYOUT_REPORT_COLUMNS.CurrencyCode.id]: false,
} as const

export const PROVET_PAY_PAYOUT_ENDPOINTS = {
  FILTERS: '/reports_v2/payouts/provet-pay/filters',
  GROUPS: '/reports_v2/payouts/provet-pay/groups',
  LIST: '/reports_v2/payouts/provet-pay/',
} as const

export const PROVET_PAY_PAYOUT_URLS = {
  FILTERS: '/{provet_id}/api/reports_v2/payouts/provet-pay/filters',
  GROUPS: '/{provet_id}/api/reports_v2/payouts/provet-pay/groups',
  LIST: '/{provet_id}/api/reports_v2/payouts/provet-pay/',
} as const

export const PROVET_PAY_PAYOUT_NON_COLUMN_FILTER_KEYS = new Set([
  'date_range',
  'group_by',
  'sort',
])

export const PROVET_PAY_PAYOUT_DEFAULT_FIELDS_REQUEST = {
  [PROVET_PAY_PAYOUT_RESOURCE_TYPES.PAYOUT]: [
    ...Object.values(PROVET_PAY_PAYOUT_REPORT_COLUMNS).map((col) => col.id),
  ],
}

export const PROVET_PAY_PAYOUT_GROUP_FIELDS_REQUEST = {
  [PROVET_PAY_PAYOUT_RESOURCE_TYPES.GROUP]: [
    'group_by',
    'group_value',
    'gross_amount',
    'fees',
    'refunds',
    'net_payout_amount',
    'payout_count',
    'currency',
  ],
}

export const PROVET_PAY_PAYOUT_EMPTY_ATTRIBUTES: Readonly<ProvetPayPayoutItemAttributes> =
  {
    channel: 'POS' as const,
    clinic_location: '',
    currency: '',
    fees: 0,
    gross_amount: 0,
    net_payout_amount: 0,
    payout_psp_reference: '',
    payout_ts_utc: '',
    refunds: 0,
    transaction_count: 0,
  } satisfies Record<keyof ProvetPayPayoutItemAttributes, unknown>
